import { useEffect, useMemo } from "react";
import { Form, Select } from "antd";
import debounce from "lodash/debounce";
import useSelectOptions from "./useFetchOptions";

export default function DynamicSelect({
  name,
  label,
  rules = [],
  required = false,
  url,
  pageSize = 10,
  visible = true,
  placeholder = "Select an option",
  className = "my-form-input-antd-test",
  variant = "nested",
  labelKey = "name",
  valueKey = "id",
  initialValue,
  takeFromInitial = false,
  multiple = false, // NEW: pass true to enable multiple selection
  maxTagCount = 3, // optional UI tweak
}) {
  const { options, loading, hasMore, setSearch, setPage, setOptions } =
    useSelectOptions({
      baseURL: url,
      initialPage: 1,
      pageSize,
      load: visible,
      takeFromInitial: takeFromInitial,
    });

  // Ensure initialValue(s) are shown in options so Select can display them.
  useEffect(() => {
    if (!initialValue) return;

    setOptions((prev) => {
      // if multiple, initialValue is expected to be an array of items or array of ids
      if (multiple) {
        // Normalize incoming initial values into objects with valueKey/labelKey if needed
        const incoming = Array.isArray(initialValue)
          ? initialValue
          : [initialValue];
        const toAdd = incoming
          .map((iv) => {
            // if iv is primitive (id), try to find label in existing options; otherwise just set id
            if (typeof iv === "object" && iv !== null) return iv;
            const found = prev.find((p) => p[valueKey] === iv);
            return found ?? { [valueKey]: iv, [labelKey]: String(iv) };
          })
          .filter(
            (item) => !prev.some((opt) => opt[valueKey] === item[valueKey])
          );

        if (toAdd.length === 0) return prev;
        return [...prev, ...toAdd];
      } else {
        // single
        const iv = initialValue;
        const exists = prev.some(
          (opt) => opt[valueKey] === (iv?.[valueKey] ?? iv)
        );
        if (exists) return prev;
        if (typeof iv === "object" && iv !== null) return [...prev, iv];
        // primitive id -> create a minimal option so Select can display it
        return [...prev, { [valueKey]: iv, [labelKey]: String(iv) }];
      }
    });
  }, [initialValue, valueKey, labelKey, setOptions, multiple]);

  // debounce + cleanup on unmount
  const debouncedSearch = useMemo(
    () =>
      debounce((val) => {
        setPage(1);
        setSearch(val);
      }, 400),
    [setSearch, setPage]
  );

  useEffect(() => {
    return () => {
      // cancel pending debounced calls on unmount
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const selectComponent = (
    <Select
      className={variant === "nested" ? className : undefined}
      placeholder={placeholder}
      showSearch
      filterOption={false}
      onSearch={debouncedSearch}
      onPopupScroll={(e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (
          scrollTop + clientHeight >= scrollHeight - 5 &&
          hasMore &&
          !loading
        ) {
          setPage((prev) => prev + 1);
        }
      }}
      loading={loading}
      mode={multiple ? "multiple" : undefined}
      optionLabelProp="children"
      maxTagCount={multiple ? maxTagCount : undefined}
      allowClear
    >
      {options.map((opt) => (
        <Select.Option key={opt[valueKey]} value={opt[valueKey]}>
          {opt[labelKey]}
        </Select.Option>
      ))}
    </Select>
  );

  // When validating "required" for multiple selects, AntD's required rule works if the form value is an array.
  const baseRules = [
    ...(required ? [{ required: true, message: `${label} is required` }] : []),
    ...rules,
  ];

  if (variant === "single") {
    return (
      <Form.Item name={name} label={label} rules={baseRules}>
        {selectComponent}
      </Form.Item>
    );
  }

  return (
    <Form.Item
      label={<p className="my-form-lables">{label}</p>}
      rules={baseRules}
    >
      <Form.Item name={name} noStyle>
        {selectComponent}
      </Form.Item>
    </Form.Item>
  );
}
